import React from "react";
import { state } from "State";
import { censusYears, communityValues, geoAggregations } from "Data";
import { geo_aggregations_lookup } from "Lookups";
import withProps from "recompose/withProps";

/**
 * @description
 * Take the newly selected year and the old year's geo_aggregation_value from the search
 * Tries to find the equalent name in the new year data set
 * If it finds it returns it otherwise an empty string is returned
 * This function is used to set the geo_aggregation_value when switching years
 * When looking for the name it removes the ` (X)` at the end of the names
 * and does the comparison
 *
 * @param  { object } param
 * @param  { string } param.yearNew
 * @param  { string } param.geoAggregationValueOld
 * @param  { string } param.geoAggregation
 */
const findGeoAggrigationValueBetweenYears = ({
  yearNew,
  geoAggregationValueOld,
  geoAggregation
}) => {
  const newNames = geoAggregations[yearNew][geoAggregation];

  const trimmedOldValue = geoAggregationValueOld
    .replace(/\([A-Z]\)$/, "")
    .trim();

  const found = newNames.find(
    n => trimmedOldValue === n.replace(/\([A-Z]\)$/, "").trim()
  );

  return found ? found : "";
};

const findCommunityValueBetweenYears = ({
  yearNew,
  communityValueOld,
  community
}) => {
  if (!community) return "";

  const newNames = communityValues[yearNew][community];

  const trimmedOldValue = communityValueOld.replace(/\([A-Z]\)$/, "").trim();

  const found = newNames.find(
    n => trimmedOldValue === n.replace(/\([A-Z]\)$/, "").trim()
  );

  return found ? found : "";
};

const PrepareCallbacks = withProps({
  // TODO: rename the data-toggle to be more specific than popover
  setCommunity: (value) => () =>
    state.select("domain_data").merge({
      community: value,
      community_value: ""
    }) &&
    state.select("ui").merge({ search_open: null, community_search_input: "" }),
  resetCommunityInputAndValue: () =>
    state.deepMerge({
      domain_data: {
        community_value: ""
      },
      ui: {
        community_search_input: "",
        search_open: ""
      }
    }),
  setCommunityValue: state.bind_set(["domain_data", "community_value"]),
  setCommunityInput: state.bind_set(["ui", "community_search_input"]),
  setYearValue: (
    value // TODO: refactor this method
  ) =>
    state.select("domain_data").merge({
      // set domain data
      year: value,
      geo_aggregation_value: findGeoAggrigationValueBetweenYears({
        yearNew: value,
        geoAggregationValueOld: state.get([
          "domain_data",
          "geo_aggregation_value"
        ]),
        geoAggregation: state.get(["domain_data", "geo_aggregation"])
      }),
      community_value: findCommunityValueBetweenYears({
        yearNew: value,
        communityValueOld: state.get(["domain_data", "community_value"]),
        community: state.get(["domain_data", "community"])
      })
    }) &&
    state.select("ui").merge({
      // set ui data
      year: value,
      geo_aggregation_search_input: findGeoAggrigationValueBetweenYears({
        yearNew: value,
        geoAggregationValueOld: state.get([
          "domain_data",
          "geo_aggregation_value",
        ]),
        geoAggregation: state.get(["domain_data", "geo_aggregation"]),
      }),
      community_search_input: findCommunityValueBetweenYears({
        yearNew: value,
        communityValueOld: state.get(["domain_data", "community_value"]),
        community: state.get(["domain_data", "community"])
      })
    }),
  setGeoAggregation: value => () =>
    state.select("domain_data").merge({
      geo_aggregation: value,
      geo_aggregation_value: ""
    }),
  openSearch: () =>
    state.get(["ui", "search_open"]) !== "primary"
      ? state.set(["ui", "search_open"], "primary")
      : null,
  closeSearch: () =>
    state.get(["ui", "search_open"]) == "primary"
      ? state.set(["ui", "search_open"], "")
      : null,
  toggleSearch: () =>
    state.get(["ui", "search_open"]) == "primary"
      ? state.set(["ui", "search_open"], "")
      : state.set(["ui", "search_open"], "primary"),
});

// check if the input is in the list of communities with name comminity
const isValidCommunityValue = (input, year, community) =>
  input == "" ? true : communityValues[year][community].indexOf(input) !== -1;

const whiteBottomBorderIfSecondarySearchBarVisible = tree =>
  !tree.domain_data.community_value || tree.ui.search_open == "primary"
    ? {}
    : { borderBottom: "solid 0.2px white" };

const Template = ({
  tree,
  setCommunity,
  setCommunityValue,
  resetCommunityInputAndValue,
  setGeoAggregation,
  setCommunityInput,
  setYearValue,
  openSearch,
  closeSearch,
  toggleSearch,
  showSearchHelp
}) =>
  // defining this function here because it depends on the actual state value
  // validate to make sure user input isn't corrupt / matches an acutal acceptable value
  (() =>
    isValidCommunityValue(
      tree.ui.community_search_input,
      tree.domain_data.year,
      tree.domain_data.community
    )
      ? // valid : set state to input value
        setCommunityValue(tree.ui.community_search_input) && closeSearch()
      : // invalid : set input to state value
        setCommunityInput(tree.domain_data.community_value)).chain(
    tryToSetCommunityValue => (
      <div id="primary-search" className="row white">
        <div className="community" style={{ flex: 1 }}>
          {/* Community  */}
          <Dropdown
            style={{ width: 165, cursor: "pointer" }}
            onChange={setCommunity}
            values={Object.keys(communityValues[tree.domain_data.year])}
            selected={tree.domain_data.community}
          />

          {/* Community Value */}
          <div
            style={{ flex: 1, height: "100%", borderBottom: "1px solid black" }}
            className={`p-0 ${!tree.domain_data.community ? "disabled" : ""} `}
          >
            <div className="btn-group align-items-center">
              <input
                style={{ height: "100%" }}
                placeholder="Search"
                type="text"
                value={tree.ui.community_search_input}
                onChange={e => setCommunityInput(e.target.value)}
                autoComplete="off"
                name="search___"
                id="search"
                disabled={!tree.domain_data.community}
                // onFocus={toggleSearch}
                onBlur={closeSearch}
                onClick={toggleSearch}
                onKeyPress={e => e.key == "Enter" && tryToSetCommunityValue()}
              />
              {tree.ui.community_search_input && (
                <a
                  style={{ paddingRight: "10px", color: "black" }}
                  href="#"
                  onClick={resetCommunityInputAndValue}
                >
                  <i className="fa fa-times-circle" />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="geo_aggregation" style={{}}>
          {/* Geo Aggregation */}
          <Dropdown2
            onChange={setGeoAggregation}
            values={Object.keys(geoAggregations[tree.domain_data.year]).sort()}
            selected={tree.domain_data.geo_aggregation}
          />
        </div>
        <div className="year" style={{}}>
          <YearDropdown
            onChange={setYearValue}
            values={censusYears}
            selected={tree.domain_data.year}
          />
        </div>
      </div>
    )
  );

// this is the community dropdown
// TODO: rename this to CommunityDropdown
const contentPopover = {
  title: "<span class='text-dark'><i class='fa fa-arrow-left' aria-hidden='true'></i>    Start by selecting an indicator</span>",
  content: "<span class='text-info'>To get started first select a community indicator and then enter a community value in the search field.</span>"
}

const Dropdown = ({ style, values, selected, onChange }) => (
  <div style={style} className="pl-3 pr-0 dropdown d-flex" 
    // data-toggle={ !selected ? "popover" : null }
    // data-html="true" 
    // data-animation="true"
    // title={contentPopover.title}
    // data-content={contentPopover.content}
    >
    <span
      className="d-flex justify-content-between align-items-center"
      id="dropdownMenuLink"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {selected || <small>select indicator</small>}
      <i className="fa fa-caret-down mr-3 ml-1" />
    </span>
    <div
      className="dropdown-menu align-items-center"
      aria-labelledby="dropdownMenuLink"
    >
      {values.map((value, index) => (
        <a
          key={index}
          onClick={onChange(value)}
          className="dropdown-item pl-3 pt-2 pb-2"
          href="#"
        >
          {value}
        </a>
      ))}
    </div>
  </div>
);

const Dropdown2 = ({ values, selected, onChange }) => (
  <div
    className="col pr-0 align-self-stretch d-flex dropdown"
    style={{ cursor: "pointer" }}
  >
    <span
      className="d-flex justify-content-between align-self-stretch align-items-center"
      id="dropdownMenuLink"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {geo_aggregations_lookup[selected]}
      <i className="fa fa-caret-down mr-3 ml-1" />
    </span>
    <div
      className="dropdown-menu align-items-center"
      aria-labelledby="dropdownMenuLink"
    >
      {values.map((value, index) => (
        <a
          key={index}
          onClick={onChange(value)}
          value={value}
          className="dropdown-item pl-3 pt-2 pb-2"
          href="#"
        >
          {geo_aggregations_lookup[value]}
        </a>
      ))}
    </div>
  </div>
);

const YearDropdown = ({ values, selected, onChange }) => (
  <div
    className="col pr-0 align-self-stretch d-flex dropdown"
    style={{ cursor: "pointer" }}
  >
    <span
      className="d-flex justify-content-between align-self-stretch align-items-center"
      id="dropdownMenuLink"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {selected}
      <i className="fa fa-caret-down mr-3 ml-1" />
    </span>
    <div
      className="dropdown-menu align-items-center"
      aria-labelledby="dropdownMenuLink"
    >
      {values.map((value, index) => (
        <a
          key={index}
          onClick={e => onChange(value)}
          value={value}
          className="dropdown-item pl-3 pt-2 pb-2"
          href="#"
        >
          {value}
        </a>
      ))}
    </div>
  </div>
);
/*
 * The Main export where we put all the Components together
 * */
export default PrepareCallbacks(Template);



// WEBPACK FOOTER //
// ./components/PrimarySearchBar.js